import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";

const formatter = (v) =>
  v.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Formato que arma la query: "id|cantidad|abreviatura|valor_envase -- ..."
const parseEnvases = (str) => {
  if (!str) return [];
  return str.split(" -- ").map((item) => {
    const [, cantidad, articulo, valor] = item.split("|");
    const cant = Number(cantidad) || 0;
    const valorEnvase = Number(valor) || 0;
    return { cantidad: cant, articulo, valorEnvase, total: cant * valorEnvase };
  });
};

// Mismo formato de medios de pago que TablaComprobantes: "[Efectivo ($58.00 -- Envase)] |-| ..."
const parseMedioPago = (str) => {
  if (!str) return [];
  return [...str.matchAll(/\[([^\]]+)\]/g)].map((m) => {
    const raw = m[1];
    const nombre = raw.split(" (")[0].trim();
    const montoMatch = raw.match(/\$\s*([0-9.,]+)/);
    const monto = montoMatch ? Number(montoMatch[1].replace(",", ".")) : 0;
    const detalleMatch = raw.match(/\$\s*[0-9.,]+\s*--\s*(.*)$/);
    const detalle = detalleMatch ? detalleMatch[1].replace(/\)$/, "") : null;
    return { nombre, monto, detalle };
  });
};

const TablaSoloEnvases = ({ pedidos }) => {
  const theme = useTheme();

  if (!Array.isArray(pedidos) || pedidos.length === 0) {
    return null;
  }

  const totalGeneral = pedidos.reduce(
    (sum, p) => sum + (Number(p.total_envases) || 0),
    0,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        mt: 3,
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          <Inventory2Icon fontSize="small" color="warning" />
          Pedidos Solo Envases
        </Typography>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>ID Pedido</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Razón Social</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>CUIT</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Dirección</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Envases</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Remito / Recibo</TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>Medios de Pago</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>
              Importe
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {pedidos.map((p, i) => {
            const envases = parseEnvases(p.envases);
            const pagos = parseMedioPago(p.medios_pago_combinados);

            return (
              <TableRow
                key={i}
                sx={{ "&:hover": { backgroundColor: "action.hover" } }}
              >
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2">#{p.id}</Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2">{p.razon_social || "-"}</Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2">{p.cuit || "-"}</Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ maxWidth: 250 }}>
                    {p.direccion_entrega || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  {envases.length === 0
                    ? "-"
                    : envases.map((e, idx) => (
                        <Typography key={idx} variant="body2">
                          {e.cantidad} x {e.articulo} (${formatter(e.valorEnvase)} c/u)
                        </Typography>
                      ))}
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2">
                    {p.fecha_para ? new Date(p.fecha_para).toLocaleDateString() : "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Typography variant="body2">
                    Remito: {p.remito_envase || "-"}
                  </Typography>
                  <Typography variant="body2">
                    Recibo: {p.recibo_envase || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  {pagos.length === 0 ? (
                    "-"
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {pagos.map((pago, idx) => (
                        <Chip
                          key={idx}
                          label={`${pago.nombre} $${formatter(pago.monto)} - ${pago.detalle}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 22 }}
                        />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${formatter(Number(p.total_envases) || 0)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}

          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell colSpan={8} align="right">
              <strong>Total Solo Envases:</strong>
            </TableCell>
            <TableCell align="right">
              <strong>${formatter(totalGeneral)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
};

export default TablaSoloEnvases;
